import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { FiltersTranslationKeys, NotificationTranslationKeys, UserTranslationKeys } from 'intl/keys';
import { createUserRoleDropdownOptions } from 'components/dataCreationForms/user/model';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { Disabled } from 'components/dataCreationForms/withInputForm/statefulInputsWrapper/types';
import {
  FormFieldProps,
  MultiChoiceFormFieldProps,
} from 'components/inspection/Form/types';
import { toLabelledEntities } from 'helpers/misc';
import { usePermissions } from 'hooks/permissions/usePermissions';
import { useCurrentUser, useCurrentUserRole } from 'store/user';
import { useMountedRef } from 'hooks/useMountRef';
import React, { memo, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { showErrorToastWithMessage } from 'services/toaster';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import { EditableStandardEntityName } from 'shared/domain/fieldValue/fields';
import { UserOnView } from 'shared/domain/user/types/view';
import { CreatableUserRole, UserRole } from 'shared/types/userRole';
import { useSites } from 'store/site';
import { useProcesses } from 'store/process';
import { UserFieldsPresentational } from './presentational';
import { UserFormProps } from './types';
import { useAdminSelection } from './useAdminSelection';
import { useVisibleNoAccess } from './useVisibleNoAccess';

const cannotEditOrganizationAdminPermissionsOnProjectDisabledReason = {
  reason: NotificationTranslationKeys.cannotEditClientAdminPermissionsOnProject,
};
const cannotEditSelfDisabledReason = {
  reason: NotificationTranslationKeys.accessSelfEditUnavailable,
};
function UserForm({
  isDialog,
  editedUserId,
  existingRoleId,
}: UserFormProps): React.ReactElement {
  const intl = useIntl();
  const isMounted = useMountedRef();
  const { _id: authorId } = useCurrentUser();
  const authorRole = useCurrentUserRole();
  const { values, setValues } = useInputForm();
  const roleValue: { _id: CreatableUserRole } = values.role;
  const sitesValue = values.sites;
  const processesValue = values.processes;
  const navigate = useNavigate();
  const { canEditUser } = usePermissions();
  const isSelfChange = editedUserId
    ? authorId === editedUserId
    : false;

  useEffect(() => {
    if (!editedUserId || !roleValue?._id) return;
    const canEditOtherUser = canEditUser(editedUserId, roleValue._id);
    if (canEditOtherUser?.permission === false && !isSelfChange) {
      showErrorToastWithMessage(canEditOtherUser?.reason ?? '');
      isMounted.current = false;
      navigate('/');
    }
  }, [editedUserId, roleValue?._id, canEditUser, isSelfChange]);

  const {
    items: sites,
  } = useSites();
  const availableSites = useMemo(
    () => toLabelledEntities(sites.filter(keepUndeleted)),
    [sites]
  );
  const { processes } = useProcesses();

  const availableProcesses = useMemo(
    () => toLabelledEntities(processes),
    [processes]
  );

  const createDialog = useDialog();

  const {
    adminSelected,
    setAdminSelected,
    handleAdminSelection,
    handleAdminDeselection,
  } = useAdminSelection({
    intl,
    createDialog,
    processesValue,
    sitesValue,
    availableSites,
    availableProcesses,
    setValues,
  });

  const { handleVisibleNoAccessSelection } = useVisibleNoAccess(setValues);
  let disabledEditPhoneAndLabel: Disabled = false;

  if (authorRole === UserRole.projectAdmin) {
    if (values.canBePersonalDataModified === false) {
      disabledEditPhoneAndLabel = {
        reason: NotificationTranslationKeys.personalDataDisabledOtherProjects,
      };
    }
  }

  let isDisabledEditRole: Disabled = false;

  if (existingRoleId === UserRole.organizationAdmin) {
    isDisabledEditRole =
      cannotEditOrganizationAdminPermissionsOnProjectDisabledReason;
  }

  if (isSelfChange) {
    isDisabledEditRole = cannotEditSelfDisabledReason;
  }

  useEffect(() => {
    if (existingRoleId === UserRole.superAdmin) {
      return;
    }
    if (roleValue?._id === existingRoleId) {
      if (
        roleValue?._id === UserRole.organizationAdmin ||
        roleValue?._id === UserRole.projectAdmin
      ) {
        isMounted.current && setAdminSelected(roleValue?._id);
        return;
      }
    }
    if (
      roleValue?._id === UserRole.standard ||
      roleValue?._id === UserRole.viewer ||
      roleValue?._id === UserRole.manager ||
      roleValue?._id === adminSelected
    ) {
      return;
    }

    if (roleValue?._id === UserRole.visible_no_access) {
      return handleVisibleNoAccessSelection();
    }

    handleAdminSelection(roleValue?._id);
  }, [
    roleValue,
    adminSelected,
    handleAdminSelection,
    existingRoleId,
    handleVisibleNoAccessSelection,
  ]);

  useEffect(() => {
    if (
      !adminSelected ||
      roleValue?._id === UserRole.projectAdmin ||
      roleValue?._id === UserRole.organizationAdmin
    ) {
      return;
    }

    isMounted.current && handleAdminDeselection();
  }, [roleValue, adminSelected, handleAdminDeselection]);

  const isVisibleNoAccessSelected =
    roleValue?._id === UserRole.visible_no_access;
  const labelFieldProps: FormFieldProps<UserOnView> = useMemo(
    () => ({
      formKey: 'label',
      localStorageKey: 'user-label',
      required: true,
      labelId: UserTranslationKeys.label,
      fieldName: 'text-field-name',
      disabled: disabledEditPhoneAndLabel,
      dense: true,
      reserveSpaceForHelperText: !isDialog && !!disabledEditPhoneAndLabel,
      'data-qa': 'access_management_name_field',
      issueParams: null,
    }),
    [isDialog, disabledEditPhoneAndLabel]
  );
  const phoneFieldProps: FormFieldProps<UserOnView> = useMemo(
    () => ({
      formKey: 'phone',
      localStorageKey: 'user-phone',
      required: true,
      disabled: disabledEditPhoneAndLabel,
      labelId: UserTranslationKeys.phone,
      fieldName: 'text-field-phone',
      dense: true,
      reserveSpaceForHelperText: !isDialog && !!disabledEditPhoneAndLabel,
      'data-qa': 'user_phone_field',
      issueParams: null,
    }),
    [isDialog, disabledEditPhoneAndLabel]
  );

  const emailFieldProps: FormFieldProps<UserOnView> = useMemo(
    () => ({
      formKey: 'email',
      required: true,
      labelId: UserTranslationKeys.email,
      fieldName: 'text-field-email',
      disabled: editedUserId
        ? { reason: NotificationTranslationKeys.cannotEditEmail }
        : false,
      dense: true,
      reserveSpaceForHelperText: !isDialog && !editedUserId,
      'data-qa': 'access_management_email_field',
      issueParams: null,
    }),
    [editedUserId, isDialog]
  );

  const roleFieldProps: FormFieldProps<UserOnView> = useMemo(
    () => ({
      formKey: 'role',
      required: true,
      labelId: UserTranslationKeys.managementRole,
      fieldName: 'singleselect-role',
      available: authorRole
        ? createUserRoleDropdownOptions(authorRole, intl)
        : [],
      getOptionLabel: (option): string =>
        formatTranslation(intl, unsafeTranslationKey(option.label) ),
      disabled: isDisabledEditRole,
      dense: true,
      reserveSpaceForHelperText: !isDialog && !isSelfChange,
      'data-qa': 'access_management_role_field',
      issueParams: null,
    }),
    [intl, authorRole, isSelfChange, isDialog, isDisabledEditRole]
  );

  const isSitesAndProcessesFieldDisabled = adminSelected
    ? { reason: UserTranslationKeys.formAdminAllSitesAllProcessesInfo }
    : isSelfChange
      ? { reason: NotificationTranslationKeys.accessSelfEditUnavailable }
      : roleValue?._id === UserRole.visible_no_access
        ? {
            reason: UserTranslationKeys.formVisibleNoAccessNoSitesNoProcessesInfo,
          }
        : false;

  const sitesFieldProps: MultiChoiceFormFieldProps<UserOnView> = useMemo(
    () => ({
      formKey: 'sites',
      required: false,
      labelId: FiltersTranslationKeys.filterTypeSite,
      fieldName: 'multiselect-site',
      available: availableSites,
      entityName: EditableStandardEntityName.location,
      disabled: isSitesAndProcessesFieldDisabled,
      reserveSpaceForHelperText:
        !isDialog && !isSitesAndProcessesFieldDisabled,
      dense: true,
      'data-qa': 'user_sites',
      isVisibleNoAccessSelected,
      issueParams: null,
    }),
    [
      availableSites,
      adminSelected,
      isSelfChange,
      isDialog,
      roleValue?._id,
      isVisibleNoAccessSelected,
      isSitesAndProcessesFieldDisabled,
    ]
  );

  const processesFieldProps: MultiChoiceFormFieldProps<UserOnView> =
    useMemo(
      () => ({
        formKey: 'processes',
        required: !(roleValue?._id === UserRole.visible_no_access),
        labelId: FiltersTranslationKeys.filterTypeProcess,
        fieldName: 'multiselect-processes',
        disabled: isSitesAndProcessesFieldDisabled,
        available: availableProcesses,
        entityName: '',
        dense: true,
        reserveSpaceForHelperText:
          !isDialog && !isSitesAndProcessesFieldDisabled,
        'data-qa': 'user_processes',
        isVisibleNoAccessSelected,
        issueParams: null,
      }),
      [
        availableSites,
        adminSelected,
        isSelfChange,
        isDialog,
        roleValue?._id,
        isVisibleNoAccessSelected,
        isSitesAndProcessesFieldDisabled,
      ]
    );

  return (
    <UserFieldsPresentational
      labelFieldProps={editedUserId ? labelFieldProps : null}
      phoneFieldProps={editedUserId ? phoneFieldProps : null}
      emailFieldProps={emailFieldProps}
      roleFieldProps={roleFieldProps}
      sitesFieldProps={sitesFieldProps}
      processesFieldProps={processesFieldProps}
    />
  );
}

export const MemoUserForm = memo(UserForm);
